"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { revalidateEntitiesCache } from "@/lib/revalidate-entities";
import { useCustomerId } from "@/contexts/customer-id-context";

/**
 * Drops everything the panel has cached about the current user's schema: the entity list
 * (Next fetch cache + React Query, which holds it at staleTime Infinity) and the ACO /
 * permission rows that decide which of those entities the user may open. A schema change
 * ships new ACOs with it, so refreshing entities alone leaves a freshly created entity
 * visible in the schema but unreadable — its list renders as "no access" until a reload.
 *
 * The refetches are awaited so a caller can navigate straight after without the old
 * schema flashing back in.
 */
export const useInvalidateSchemaCache = () => {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();

  return useCallback(async () => {
    await revalidateEntitiesCache();
    await queryClient.cancelQueries({ queryKey: ["entities", customerId] });
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["entities", customerId] }),
      // Both permission queries ("myPermissions" for the current user, "acos") live under
      // this prefix; useInitializePermissions rebuilds the zustand store from them.
      queryClient.refetchQueries({ queryKey: ["permissions"] }),
    ]);
  }, [queryClient, customerId]);
};
